import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { FindingLabel } from '@/models/template';
import { ClassPicker } from '../primitives/ClassPicker';
import { EditableText } from '../primitives/EditableText';
import { RowControls } from '../primitives/RowControls';
import styles from './EditableFindingsSection.module.css';

interface EditableSplitFindingRowProps {
  label: FindingLabel;
  position: string;
  findingClasses: string[];
  usedClasses: Set<string>;
  onChange: (label: FindingLabel) => void;
  onDelete: () => void;
}

export function EditableSplitFindingRow({
  label,
  position,
  findingClasses,
  usedClasses,
  onChange,
  onDelete,
}: EditableSplitFindingRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: label.uuid });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleLabelChange = (value: string) => {
    onChange({ ...label, label: value });
  };

  const handleClassChange = (next: string) => {
    onChange({ ...label, findingClass: next });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={styles.splitRow}
      data-editable-row
      {...attributes}
      {...listeners}
    >
      <div className={styles.splitRowHeader}>
        <span className={styles.positionBadge}>{position}.</span>
        <span className={styles.splitRowLabel}>
          <EditableText
            value={label.label}
            onChange={handleLabelChange}
            placeholder="Finding name"
          />
        </span>
        <ClassPicker
          value={label.findingClass}
          options={findingClasses}
          usedClasses={usedClasses}
          onChange={handleClassChange}
          placeholder="Pick class"
        />
        <RowControls onDelete={onDelete} />
      </div>
      <div className={styles.splitRowPlaceholder}>
        <span className={styles.splitRowPlaceholderText}>
          Reader enters text here…
        </span>
      </div>
    </div>
  );
}
